import { get } from '../components/library/api/rest/index';
import { unwrapXmlStyleContent } from '../utils/apiFieldText';
function normalizeRow(row) {
    if (!row || typeof row !== 'object')
        return {};
    const out = {};
    for (const [k, v] of Object.entries(row)) {
        out[k.toLowerCase()] = v;
    }
    return out;
}
function readString(norm, keys) {
    for (const key of keys) {
        const v = norm[key.toLowerCase()];
        if (v == null)
            continue;
        if (typeof v === 'string' && v.trim())
            return unwrapXmlStyleContent(v);
        if (typeof v === 'number' && Number.isFinite(v))
            return String(v);
    }
    return '';
}
function readBoolean(norm, keys, fallback) {
    const raw = readString(norm, keys).trim().toLowerCase();
    if (!raw)
        return fallback;
    return raw === 'y' || raw === 'yes' || raw === 'true' || raw === '1';
}
function gridDataArray(res) {
    const d = res.Data ?? res.data;
    return Array.isArray(d) ? d : [];
}
/** Row shape: `TITLE`, `MESSAGE`, `ICON` (Material icon ligature, e.g. `warning_amber`), `BGCOLOR`/`TEXTCOLOR` (theme palette paths, e.g. `warning.main`). `SYSTEM_STATUS`/`IS_SYSTEM` are filtered server-side by the grid request. */
function mapRowToSystemBanner(row) {
    const norm = normalizeRow(row);
    const title = readString(norm, ['title']);
    const message = readString(norm, ['message']);
    if (!title.trim() && !message.trim())
        return null;
    return {
        title: title || undefined,
        message: message || undefined,
        icon: readString(norm, ['icon']) || undefined,
        backgroundColor: readString(norm, ['bgcolor', 'background_color']) || undefined,
        textColor: readString(norm, ['textcolor', 'text_color']) || undefined,
        dismissible: readBoolean(norm, ['dismissible'], true),
        /* Omitted (not forced false) so SystemBanner's own default (always sticky, like the navbar) applies
           unless a row explicitly opts out with STICKY = 'N'. */
        ...(readString(norm, ['sticky']) ? { sticky: readBoolean(norm, ['sticky'], true) } : {}),
    };
}
async function fetchSystemBannerRowsKeyed(sidebarBase, flowId, pageSize, scope) {
    const root = sidebarBase.replace(/\/$/, '');
    const params = new URLSearchParams();
    const hasScope = Boolean(scope?.agencyName?.trim() || scope?.permitTitle?.trim());
    let filter = hasScope ? "SYSTEM_STATUS~eq~'A'" : "SYSTEM_STATUS~eq~'A'~and~IS_SYSTEM~eq~'Y'";
    if (scope?.agencyName && scope.agencyName.trim()) {
        const safe = scope.agencyName.trim().replace(/'/g, "''");
        filter += `~and~AGENCY~contains~'${safe}'`;
    }
    if (scope?.permitTitle && scope.permitTitle.trim()) {
        const safe = scope.permitTitle.trim().replace(/'/g, "''");
        filter += `~and~TITLE~contains~'${safe}'`;
    }
    params.set('filter', filter);
    params.set('page', '1');
    params.set('pageSize', String(pageSize));
    const url = `${root}/api/PublicFlowBuilderRecord/Grid/${encodeURIComponent(flowId)}?${params.toString()}`;
    const json = (await get(url, null, null, null));
    if (!json)
        return [];
    const rows = gridDataArray(json);
    const out = [];
    for (const row of rows) {
        const banner = mapRowToSystemBanner(row);
        if (!banner)
            continue;
        const norm = normalizeRow(row);
        const rowId = readString(norm, ['id', 'recordid']);
        const key = rowId || `${banner.title ?? ''} ${banner.message ?? ''}`;
        /* Only the general/unscoped (IS_SYSTEM) banner caps+scrolls a long message — an agency- or
           permit-scoped one always shows the full message, since it isn't sharing the page-top strip
           with other site-wide notices the way the general one is. */
        out.push({ key, banner: { ...banner, scrollableMessage: !hasScope } });
    }
    return out;
}
function dedupeKeyed(keyed) {
    const seen = new Set();
    const out = [];
    for (const { key, banner } of keyed) {
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(banner);
    }
    return out;
}
/**
 * Fetches every active (`SYSTEM_STATUS = 'A'`) system banner row matching `scope` from a Flow
 * Builder grid and maps each to {@link SystemBannerProps} — e.g. two independent, unrelated
 * outage notices can both be active for the same agency at once, and both must show, not just
 * whichever the API happens to list first. Returns `[]` when nothing matches. `IS_SYSTEM = 'Y'`
 * is only added when `scope` is empty (the general, unscoped banner) — an agency- and/or
 * permit-scoped request drops it, since scoped rows aren't marked `IS_SYSTEM`.
 */
export async function fetchSystemBannerRows(sidebarBase, flowId, pageSize = 1000, scope) {
    return dedupeKeyed(await fetchSystemBannerRowsKeyed(sidebarBase, flowId, pageSize, scope));
}
/**
 * Same as {@link fetchSystemBannerRows}, but for several scopes fetched and shown together (e.g.
 * general + agency + permit, stacked on the same page) — dedupes across ALL of them combined, so
 * one row that happens to match two scopes at once (e.g. its `AGENCY` and `TITLE` both match) only
 * renders once instead of once per scope that matched it.
 */
export async function fetchSystemBannerRowsMultiScope(sidebarBase, flowId, pageSize = 1000, scopes = []) {
    const perScope = await Promise.all(scopes.map((scope) => fetchSystemBannerRowsKeyed(sidebarBase, flowId, pageSize, scope)));
    return dedupeKeyed(perScope.flat());
}
